import { type FC, useLayoutEffect, useRef, useState } from 'react';
import { Input } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import type { FindingHierarchyCompletionProps } from './types/findingHierarchy';
import { useHierarchyCompletion } from './hooks/useHierarchyCompletion';
import { DropdownPanel } from './DropdownPanel';
import styles from './FindingHierarchyCompletion.module.css';

const { TextArea } = Input;

const FindingHierarchyCompletion: FC<FindingHierarchyCompletionProps> = ({
  value,
  onChange,
  attributes,
  regionLabel,
  disabled,
  readOnly = false,
  findingClassLabels,
  selectedStatements,
  onSetStatements,
}) => {
  const hook = useHierarchyCompletion({
    value,
    onChange,
    attributes,
    regionLabel,
    findingClassLabels,
    selectedStatements,
    onSetStatements,
  });

  // The dropdown anchors to the FindingsPanel `.list` (the worksheet section)
  // so it can span the full width. We measure the inputBox's bottom edge
  // relative to that positioning context and pass it as the dropdown's `top`.
  //
  // The edge moves as the input grows: selecting a pill inserts a statement
  // row above the add-input, and typing autosizes the textarea. We recompute
  // on those changes (deps) and via a ResizeObserver on the box (catches the
  // textarea's async height sync) so the dropdown stays pinned to the last
  // line of input and the newly added row stays visible.
  const inputBoxRef = useRef<HTMLDivElement>(null);
  const [topOffset, setTopOffset] = useState(0);
  useLayoutEffect(() => {
    if (!hook.open) return undefined;
    const box = inputBoxRef.current;
    if (!box) return undefined;
    const measure = () => setTopOffset(box.offsetTop + box.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [hook.open, value, selectedStatements]);

  return (
    <div className={styles.wrapper} ref={hook.wrapperRef}>
      <div className={styles.inputBox} ref={inputBoxRef}>
        <TextArea
          ref={hook.textAreaRef}
          value={value}
          data-committed=""
          rows={1}
          readOnly={readOnly}
          onChange={readOnly ? undefined : hook.handleChange}
          onKeyDown={readOnly ? undefined : hook.handleKeyDown}
          onBlur={readOnly ? undefined : hook.handleBlur}
          onMouseDown={readOnly ? undefined : hook.handleMouseDown}
          onFocus={readOnly ? undefined : hook.onTextAreaFocus}
          disabled={disabled}
        />

        {!disabled && value && (
          <CloseCircleFilled
            onClick={hook.handleClear}
            className={styles.clearIcon}
          />
        )}
      </div>

      <DropdownPanel
        open={hook.open}
        disabled={disabled}
        visibleOptions={hook.visibleOptions}
        gridRow={hook.gridRow}
        gridCol={hook.gridCol}
        setGridPos={hook.setGridPos}
        onSuppressBlur={hook.suppressBlur}
        onClearKeyboardNav={hook.clearKeyboardNav}
        onSelectLeaf={hook.selectLeaf}
        onToggleAllNormal={hook.toggleAllNormal}
        topOffset={topOffset}
        selectedStatements={selectedStatements}
        focusedPillDomRef={hook.focusedPillDomRef}
      />
    </div>
  );
};

export default FindingHierarchyCompletion;
